import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './NotificationPage.css';
import {
  Bell, Check, X, Eye, EyeOff, Clock, MoreVertical, ChevronRight,
  Search, Filter, Settings, Users, MessageCircle, Briefcase, Calendar,
  TrendingUp, Award, Edit, Building, ThumbsUp, Share2, UserPlus,
  Hash, Video, Bookmark, Sparkles, Inbox, Loader2
} from 'lucide-react';

const NotificationPage = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filterOptions, setFilterOptions] = useState([]);
  const [popularTopics, setPopularTopics] = useState([]);
  const [insights, setInsights] = useState({ total: 0, unread: 0, engagementRate: 0 });
  const [loading, setLoading] = useState({
    notifications: false,
    filters: false,
    popular: false,
    insights: false
  });
  const [error, setError] = useState({});

  const API_BASE_URL = `${process.env.REACT_APP_API_URL}`;

  const fetchNotifications = useCallback(async () => {
    setLoading(prev => ({ ...prev, notifications: true }));
    try {
      const token = localStorage.getItem("token");
      const params = {};
      if (activeTab !== 'all') params.filter = activeTab;
      if (searchTerm) params.search = searchTerm;
      if (selectedFilters.length) params.categories = selectedFilters.join(',');
      
      const res = await axios.get(`${API_BASE_URL}/notification`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unreadCount || 0);
    } catch (err) {
      setError(prev => ({ ...prev, notifications: err.message }));
    } finally {
      setLoading(prev => ({ ...prev, notifications: false }));
    }
  }, [activeTab, searchTerm, selectedFilters]);

  const fetchFilterOptions = async () => {
    setLoading(prev => ({ ...prev, filters: true }));
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE_URL}/notification/filters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFilterOptions(res.data.filters || []);
    } catch (err) {
      setError(prev => ({ ...prev, filters: err.message }));
    } finally {
      setLoading(prev => ({ ...prev, filters: false }));
    }
  };

  const fetchPopularTopics = async () => {
    setLoading(prev => ({ ...prev, popular: true }));
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE_URL}/notification/popular`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPopularTopics(res.data.topics || []);
    } catch (err) {
      setError(prev => ({ ...prev, popular: err.message }));
    } finally {
      setLoading(prev => ({ ...prev, popular: false }));
    }
  };

  const fetchInsights = async () => {
    setLoading(prev => ({ ...prev, insights: true }));
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE_URL}/notification/insights`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInsights(res.data.insights || { total: 0, unread: 0, engagementRate: 0 });
    } catch (err) {
      setError(prev => ({ ...prev, insights: err.message }));
    } finally {
      setLoading(prev => ({ ...prev, insights: false }));
    }
  };

  useEffect(() => {
    fetchNotifications();
    fetchFilterOptions();
    fetchPopularTopics();
    fetchInsights();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => fetchNotifications(), 500);
    return () => clearTimeout(timer);
  }, [activeTab, selectedFilters, searchTerm]);

  const toggleRead = async id => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.patch(`${API_BASE_URL}/notification/${id}/toggle-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, read: res.data.read } : n));
      setUnreadCount(prev => res.data.read ? prev - 1 : prev + 1);
    } catch (err) {
      console.error('Error toggling notification:', err);
    }
  };

  const markAllRead = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API_BASE_URL}/notification/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const deleteNotification = async id => {
    try {
      const token = localStorage.getItem("token");
      const deleted = notifications.find(n => n._id === id);
      await axios.delete(`${API_BASE_URL}/notification/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.filter(n => n._id !== id));
      if (deleted && !deleted.read) setUnreadCount(prev => prev - 1);
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  const updateSettings = async settings => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API_BASE_URL}/users/notification-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowSettings(false);
    } catch (err) {
      console.error('Error updating settings:', err);
    }
  };

  const getIcon = (type, color) => {
    const icons = {
      reaction: ThumbsUp,
      connection: UserPlus,
      comment: MessageCircle,
      job: Briefcase,
      share: Share2,
      event: Calendar,
      trending: TrendingUp,
      milestone: Award,
      mention: Edit,
      company: Building,
      default: Bell
    };
    const Icon = icons[type] || icons.default;
    return <Icon size={20} style={{ color }} />;
  };

  const tabs = [
    { id: 'all', label: 'All', icon: Bell },
    { id: 'unread', label: 'Unread', icon: Eye },
    { id: 'mentions', label: 'Mentions', icon: Edit },
    { id: 'jobs', label: 'Jobs', icon: Briefcase }
  ];

  const settingItems = [
    { key: 'pushNotifications', icon: Bell, label: 'Push Notifications', desc: 'Receive notifications on your device' },
    { key: 'connectionRequests', icon: UserPlus, label: 'Connection Requests', desc: 'When someone wants to connect' },
    { key: 'commentsAndMentions', icon: MessageCircle, label: 'Comments & Mentions', desc: 'When people interact with your content' },
    { key: 'jobAlerts', icon: Briefcase, label: 'Job Alerts', desc: 'New opportunities matching your profile' },
    { key: 'eventsAndWebinars', icon: Calendar, label: 'Events & Webinars', desc: 'Upcoming professional events' }
  ];

  const quickActions = [
    { icon: Bookmark, label: 'Saved Jobs', color: '#8d65dd' },
    { icon: Users, label: 'Network', color: '#3b82f6' },
    { icon: Edit, label: 'Post Update', color: '#10b981' },
    { icon: Share2, label: 'Share Profile', color: '#f59e0b' }
  ];

  const footerLinks = ['Notification Help', 'Privacy Settings', 'Manage Preferences', 'Report an Issue'];

  return (
    <div className="notif-page">
      {/* Background Elements */}
      <div className="notif-bg">
        <div className="notif-gradient"></div>
        <div className="notif-pattern"></div>
      </div>

      {/* Header */}
      <header className="notif-header">
        <div className="notif-header-content">
          <div className="notif-header-top">
            <div className="notif-title-section">
              <h1 className="notif-title">Notifications</h1>
              <div className="notif-badge">
                <Bell size={16} className='bell-icon' />
                <span className="notif-badge-count">{unreadCount}</span>
                <span className="notif-badge-label">unread</span>
              </div>
            </div>
            
            <div className="notif-header-actions">
              <button className="notif-btn-mark" onClick={markAllRead}>
                <Check size={16} />
                <span>Mark all read</span>
              </button>
              <button 
                className={`notif-btn-icon ${showSettings ? 'active' : ''}`}
                onClick={() => setShowSettings(!showSettings)}
              >
                <Settings size={18} />
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="notif-search">
            <Search className="notif-search-icon" size={18} />
            <input
              type="text"
              placeholder="Search notifications"
              className="notif-search-input"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            <button 
              className={`notif-filter-btn ${showFilters ? 'active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter size={18} />
              <span>Filters</span>
              {selectedFilters.length > 0 && (
                <span className="notif-filter-count">{selectedFilters.length}</span>
              )}
            </button>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="notif-filters-panel">
              <h4>Filter by category</h4>
              <div className="notif-filter-grid">
                {filterOptions.map(filter => (
                  <button
                    key={filter.id}
                    className={`notif-filter-chip ${selectedFilters.includes(filter.id) ? 'active' : ''}`}
                    onClick={() => setSelectedFilters(prev => 
                      prev.includes(filter.id)
                        ? prev.filter(id => id !== filter.id)
                        : [...prev, filter.id]
                    )}
                  >
                    <span>{filter.label}</span>
                    <span className="notif-chip-count">{filter.count}</span>
                  </button>
                ))}
              </div>
              
              <div className="notif-filter-actions">
                <button className="notif-filter-clear" onClick={() => setSelectedFilters([])}>
                  Clear all
                </button>
                <button className="notif-filter-apply" onClick={() => setShowFilters(false)}>
                  Apply
                </button>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="notif-tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`notif-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <tab.icon size={16} />
                <span>{tab.label}</span>
                {tab.id === 'unread' && unreadCount > 0 && (
                  <span className="notif-tab-badge">{unreadCount}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="notif-main">
        <div className="notif-container">
          {/* Left Column - Notifications List */}
          <div className="notif-list-column">
            <div className="notif-list">
              {loading.notifications ? (
                <div className="notif-state">
                  <Loader2 size={32} className="spin" />
                  <p>Loading notifications...</p>
                </div>
              ) : error.notifications ? (
                <div className="notif-state error">
                  <X size={32} />
                  <p>Error: {error.notifications}</p>
                  <button onClick={fetchNotifications} className="notif-retry-btn">
                    <Loader2 size={14} className="spin" />
                    Retry
                  </button>
                </div>
              ) : notifications.length > 0 ? (
                notifications.map(notification => (
                  <div key={notification._id} className={`notif-item ${notification.read ? 'read' : 'unread'}`}>
                    <div className="notif-item-icon">
                      {getIcon(notification.type, notification.color)}
                    </div>
                    
                    <div className="notif-item-content">
                      <div className="notif-item-header">
                        <h3 className="notif-item-title">{notification.title}</h3>
                        <div className="notif-item-time">
                          <Clock size={12} />
                          <span>{notification.time}</span>
                        </div>
                      </div>
                      
                      <p className="notif-item-desc">{notification.description}</p>
                      
                      <div className="notif-item-actions">
                        <button className="notif-action primary" onClick={() => {}}>
                          <span>View</span>
                          <ChevronRight size={14} />
                        </button>
                        <button 
                          className="notif-action secondary"
                          onClick={() => toggleRead(notification._id)}
                        >
                          {notification.read ? <EyeOff size={14} /> : <Eye size={14} />}
                          <span>{notification.read ? 'Unread' : 'Read'}</span>
                        </button>
                        <button 
                          className="notif-action delete"
                          onClick={() => deleteNotification(notification._id)}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                    
                    <button className="notif-item-more">
                      <MoreVertical size={16} />
                    </button>
                  </div>
                ))
              ) : (
                <div className="notif-state">
                  <Inbox size={48} />
                  <h3>All caught up!</h3>
                  <p>No notifications to show</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="notif-sidebar">
            {/* Settings Panel */}
            {showSettings && (
              <div className="notif-card settings">
                <div className="notif-card-header">
                  <h3>Notification Settings</h3>
                  <button className="notif-card-close" onClick={() => setShowSettings(false)}>
                    <X size={16} />
                  </button>
                </div>
                
                <div className="notif-settings-list">
                  {settingItems.map(item => (
                    <div key={item.key} className="notif-setting-item">
                      <div className="notif-setting-info">
                        <item.icon size={16} />
                        <div>
                          <h4>{item.label}</h4>
                          <p>{item.desc}</p>
                        </div>
                      </div>
                      <label className="notif-switch">
                        <input type="checkbox" />
                        <span className="notif-slider"></span>
                      </label>
                    </div>
                  ))}
                </div>
                
                <button className="notif-save-btn">Save Preferences</button>
              </div>
            )}

            {/* Insights Card */}
            <div className="notif-card">
              <div className="notif-card-header">
                <h3>Insights</h3>
                <TrendingUp size={16} />
              </div>
              
              <div className="notif-stats">
                <div className="notif-stat">
                  <div className="notif-stat-value">{insights.total}</div>
                  <div className="notif-stat-label">Total</div>
                </div>
                <div className="notif-stat">
                  <div className="notif-stat-value">{insights.unread}</div>
                  <div className="notif-stat-label">Unread</div>
                </div>
                <div className="notif-stat">
                  <div className="notif-stat-value">{insights.engagementRate || 0}%</div>
                  <div className="notif-stat-label">Engagement</div>
                </div>
              </div>
            </div>

            {/* Popular Topics */}
            {popularTopics.length > 0 && (
              <div className="notif-card">
                <div className="notif-card-header">
                  <h3>Popular This Week</h3>
                  <Sparkles size={16} />
                </div>
                <div className="notif-topics">
                  {popularTopics.map((topic, index) => (
                    <div key={index} className="notif-topic">
                      <div className="notif-topic-icon">
                        {topic.icon === 'hash' && <Hash size={16} />}
                        {topic.icon === 'video' && <Video size={16} />}
                        {topic.icon === 'briefcase' && <Briefcase size={16} />}
                      </div>
                      <div className="notif-topic-content">
                        <h4>{topic.title}</h4>
                        <p>{topic.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="notif-card">
              <h3>Quick Actions</h3>
              <div className="notif-quick-actions">
                {quickActions.map(action => (
                  <button key={action.label} className="notif-quick-btn">
                    <action.icon size={16} style={{ color: action.color }} />
                    <span>{action.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Footer Links */}
            <div className="notif-footer-links">
              {footerLinks.map(link => (
                <a key={link} href="#" className="notif-footer-link">
                  {link}
                </a>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default NotificationPage;